import { useState, useRef } from "react";
import styled from "styled-components";
import { Tooltip } from "react-tooltip";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { COUNTRIES, COUNTRY_ICONS } from "constants/countries";
import { dateFormatted } from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import SectionTitle from "components/text/SectionTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ContextMenu from "components/menu/ContextMenu";
import ToggleSwitch from "components/forms/ToggleSwitch";

import blockIcon from "assets/img/icons/icons8-unavailable.svg";
import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";

const AutoresponderMessages = styled.div``;

const Header = styled.div`
  margin-bottom: 12px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const FilterWrap = styled.div`
  position: relative;
`;

const MenuIconBox = styled.div`
  position: relative;
`;

const ActiveStatusDot = styled.div`
  position: absolute;
  top: 0;
  left: 25px;

  z-index: 100;
`;

const StatusDot = styled.div`
  width: 12px;
  min-width: 12px;
  height: 12px;

  border-radius: 50%;

  background: ${(props) => props.color};
`;

const FilterMenu = styled.div`
  position: absolute;
  top: 42px;
  right: 0;
  z-index: 10;

  transition: 0.2s;

  opacity: ${(props) => (props.opened ? 100 : 0)};
  pointer-events: ${(props) => (props.opened ? "auto" : "none")};
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const OptionText = styled.div``;

const Messages = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  min-width: 520px;

  border-spacing: 0;
`;

const TableHead = styled.thead``;

const Label = styled.th`
  padding: 0 5px 12px 5px;
  max-width: ${(props) => (props.width ? props.width : "none")};

  font-size: 17px;
  font-weight: 400;
  text-align: left;
`;

const TableBody = styled.tbody``;

const Cell = styled.td`
  padding: 8px 4px;
  max-width: ${(props) => (props.width ? props.width : "none")};

  white-space: nowrap;
  font-size: 15px;
`;

const CellVeryLeft = styled.div`
  padding-left: 18px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-left: 10px;
  }
`;

const CellVeryRight = styled.div`
  padding-right: 18px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-right: 10px;
  }
`;

const CellLineValueWrap = styled.div`
  padding-right: 10px;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoResults = styled.div`
  width: 100%;
  margin: 25px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div``;

const EllipsisValue = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default ({
  data,
  openMessage,
  onStatusToggleClick,
  loadingStatusToggle,
  onEditClick,
}) => {
  const list = data.responderMessages.map((message) => {
    const { id, order, status, subject } = message;

    return (
      <tr key={id} onClick={() => openMessage(id)}>
        <Cell>{order}</Cell>
        <Cell>
          <CellLineValueWrap>{subject}</CellLineValueWrap>
        </Cell>
        <Cell>0</Cell>
        <Cell>
          <ToggleSwitch
            on={status === "active"}
            onClick={() => onStatusToggleClick(id)}
            loading={loadingStatusToggle}
          />
        </Cell>
        <Cell></Cell>
        <Cell>
          <ButtonRoundIcon
            icon={pencilIcon}
            hoverIcon={pencilWhiteIcon}
            background={css.BRAND_YELLOW}
            hoverBackground={css.BRAND_GREEN}
            onClick={() => onEditClick(id)}
            buttonSize="24px"
            iconSize="12px"
          />
        </Cell>
      </tr>
    );
  });

  return (
    <AutoresponderMessages>
      <Header>
        <Title>Email Messages</Title>
      </Header>
      <Messages>
        <TableWrap className="table-wrap">
          {list.length > 0 && (
            <ListTable>
              <TableHead>
                <tr>
                  <Label>Day</Label>
                  <Label>Message</Label>
                  <Label>Sent</Label>
                  <Label>Status</Label>
                  <Label>Reorder</Label>
                  <Label></Label>
                </tr>
              </TableHead>
              <TableBody>{list}</TableBody>
            </ListTable>
          )}
          {list.length === 0 && (
            <NoResults>
              <NoResultsIcon>
                <img src={blockIcon} style={{ width: "100%" }} />
              </NoResultsIcon>
              <NoResultsText>
                The autoresponder does not have any messages created yet.
              </NoResultsText>
            </NoResults>
          )}
        </TableWrap>
      </Messages>
    </AutoresponderMessages>
  );
};
